



















































































import mixins from 'vue-typed-mixins';
import VatsAPI from '@/Shared/VatsAPI';
import LocationBasedMixin from '@/Shared/LocationBasedMixin';
import PermitVariables from './PermitVariables';
import PermitType from './PermitType';
import { ExpirationPeriodType } from './Enum';
import { Logger } from '@/Shared/Logger';
import I18nMixin from '@/Shared/I18nMixin';
import { getValidationErrors } from '@/Shared/ValidationErrorDirective';
import CancelTokenMixin from '@/Shared/CancelTokenMixin';
import UserMixin from '@/Shared/UserMixin';
import { ILookupType, LocationLookupType } from '@/Stores/LookupStore';
import { getQuarterCount, getDaysMonth, countDays } from '@/Shared/DateFunctions';

const PermitTypeSelect = mixins(LocationBasedMixin, I18nMixin, CancelTokenMixin, UserMixin).extend({
    name: 'PermitTypeSelect',
    model: {
        prop: 'value',
        event: 'change'
    },
    props: {
        settings: {
            required: true,
            type: Object as () => PermitVariables
        },
        value: {
            required: true,
            type: Object as () => PermitType
        }
    },
    data() {
        return {
            permitTypes: [] as PermitType[],
            permitCategories: [] as ILookupType[],
            selectedCategory: null as ILookupType | null,
            selected: this.value as PermitType | null,
            selectedOriginal: { ...this.value },
            selectedPermitTypeID: '',
            computedExpirationDate: null as Date | null,
            computedStartDate: null as Date | null,
            minStartDate: null as Date | null,
            maxEndDate: null as Date | null,
            duration: 0 as number,
            overridesEnabled: false,
            validationErrors: {} as { [propName: string]: string[] },
            itemsLoading: 0
        };
    },
    async mounted() {
        await this.loadUser();
        await this.loadPermitCategories();
        await this.refreshPermitTypes();
    },
    watch: {
        value(newValue) {
            this.selected = newValue;
        },
        async selected() {
            if (this.selected) {
                this.selectedOriginal = { ...this.selected };
                if (this.selected.requiresDuration) {
                    this.duration = 1;
                }
            }
            this.computedStartDate = null;
            this.computedExpirationDate = null;
            await this.computeStartDate();
            await this.computeExpirationDate();
            this.$emit('changeDate', { startDate: this.computedStartDate, endDate: this.computedExpirationDate });

        },
        async selectedPermitTypeID() {
            this.overridesEnabled = false;

            let permitType = null;

            this.itemsLoading++;
            try {
                permitType = (
                    await VatsAPI.get<PermitType>(
                        `/${this.location}/Permits/type`, { params: { description: this.selectedPermitTypeID } }
                    )
                ).data;

                this.validationErrors = {};
            }
            catch (e) {
                this.$bvToast.toast(this.i18nLocal('typeLoadError').toString(), {
                    title: this.$i18n.t('common.error').toString(),
                    autoHideDelay: 5000
                });

                this.validationErrors = {...this.validationErrors, selectedPermitTypeID: ['Invalid Permit Type']};

                Logger.Error('Error loading permit type', { error: e });
            }
            finally {
                this.itemsLoading--;
            }

            this.$emit('change', permitType);
        },
        async duration() {
            this.validationErrors = {};
            if (this.selected) {
                this.selected.duration = this.duration;
                this.selected.cost = this.selectedOriginal.requiresDuration && this.duration > 0 ?
                    this.selectedOriginal.cost * this.duration :
                    this.selectedOriginal.cost;
            }
            await this.computeExpirationDate();
            this.$emit('changeDate', { startDate: this.computedStartDate, endDate: this.computedExpirationDate });
        },
        isInternal() {
            this.refreshPermitTypes();
        }
    },
    computed: {
        i18nPrefix() { return 'permits.typeSelect'; },
        loading(): boolean {
            return this.itemsLoading > 0;
        },
        expirationTypeLabel(): string {
            const label = this.selected?.expirationType ?
                this.getExpirationTypeLabelFromPermitExpirationType(this.selected.expirationType) : '';

            return label;
        },
        isPermitTypeSelected(): boolean {
            return !!this.selectedPermitTypeID
                && !!this.selectedPermitTypeID.length;
        },
        permitTypesUrl(): string {
            return `/${this.location}/permits/Get${this.isInternal ? '' : 'Online'}PermitTypes`;
        },
        locationHasCategories(): boolean {
            return !!this.permitCategories.length;
        },
        filteredPermitTypes(): PermitType[] {
            if (this.selectedCategory === null) {
                return this.permitTypes;
            }

            const filtered = this.permitTypes.filter(p => p.categoryCode === this.selectedCategory?.code);
            return filtered;
        }
    },
    methods: {
        async loadPermitCategories() {
            await this.$typedStore.modules.lookupsModule.actions
                .getLocationLookups({
                    lookupType: LocationLookupType.PermitCategory,
                    location: this.location
                })
                .then(categories => {
                        this.permitCategories = Object.keys(categories).map(key => categories[key]);
                });
        },
        async refreshPermitTypes() {
            const token = this.createCancelToken('refreshPermitTypes');
            this.itemsLoading++;
            try {
                this.permitTypes = (await VatsAPI.get(
                    this.permitTypesUrl,
                    { cancelToken: token })).data;
            }
            catch (e) {
                this.throwIfNotCancel(e);
            }
            finally {
                this.itemsLoading--;
            }
        },
        async computeExpirationDate() {
            try {
                const expirationDate = this.selectedPermitTypeID !== '' ?
                    (await this.getExpirationDate()) : new Date();

                this.computedExpirationDate = new Date(expirationDate!);
                this.computedExpirationDate = new Date(this.computedExpirationDate.getFullYear(),
                this.computedExpirationDate.getMonth(),
                this.computedExpirationDate.getDate());
                await this.checkExpirationDate();
            }
            catch (e) {
                this.validationErrors = {...this.validationErrors, ...getValidationErrors(e.response)};
                Logger.Log('Validation Errors', this.validationErrors);
            }
        },
        async computeStartDate() {
            const startDate = this.selectedPermitTypeID !== '' ?
                (await this.getStartDate()) : new Date();

            this.computedStartDate = new Date(startDate!);
            this.computedStartDate = new Date(this.computedStartDate.getFullYear(),
            this.computedStartDate.getMonth(),
            this.computedStartDate.getDate());
            this.checkMinStartDate();
        },
        async checkMinStartDate() {
            const type = this.selected?.expirationType
            ? this.getExpirationPeriodType(this.selected.expirationType)
            : null;
            if (type === null) {
                this.minStartDate = this.computedStartDate;
            }
            if (type === ExpirationPeriodType.Annual) {
                const ystart = new Date(new Date().getFullYear(), 0, 1);
                if (this.computedStartDate! < ystart) {
                    this.computedStartDate = ystart;
                    this.minStartDate = ystart;
                } else {
                    this.minStartDate = this.computedStartDate;
                }
            }

            if (type === ExpirationPeriodType.Daily
            || type === ExpirationPeriodType.SpecificDate
            || type === ExpirationPeriodType.NonExpiring) {
                const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                if (this.computedStartDate! < today) {
                    this.computedStartDate = today;
                    this.minStartDate = today;
                } else {
                    this.minStartDate = this.computedStartDate;
                }
            }

            if (type === ExpirationPeriodType.Monthly) {
                const mstart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                if (this.computedStartDate! < mstart) {
                    this.computedStartDate = mstart;
                    this.minStartDate = mstart;
                } else {
                    this.minStartDate = this.computedStartDate;
                }
            }

            if (type === ExpirationPeriodType.Quarterly) {
                const q = Math.floor(this.computedStartDate!.getMonth() / 3);
                const qstart =  new Date(this.computedStartDate!.getFullYear(), q * 3, 1);
                if (this.computedStartDate! < qstart) {
                    this.computedStartDate = qstart;
                    this.minStartDate = qstart;
                } else {
                    this.minStartDate = this.computedStartDate;
                }
            }
        },
        async locationChanged() {
            this.selectedPermitTypeID = '';

            // reset selected on location change
            this.$emit('change', {});
        },
        getExpirationPeriodType(expirationType: string): ExpirationPeriodType {
            let expType = ExpirationPeriodType.NonExpiring;

            expirationType = expirationType.toUpperCase();

            if (expirationType === 'ANN') {
                expType = ExpirationPeriodType.Annual;
            }
            else if (expirationType === 'NOE') {
                expType = ExpirationPeriodType.NonExpiring;
            }
            else if (expirationType === 'DAT') {
                expType = ExpirationPeriodType.SpecificDate;
            }
            else if (expirationType.indexOf('M') === 0) {
                expType = ExpirationPeriodType.Monthly;
            }
            else if (expirationType.indexOf('D') === 0) {
                expType = ExpirationPeriodType.Daily;
            }
            else if (expirationType.indexOf('Q') === 0) {
                expType = ExpirationPeriodType.Quarterly;
            }

            return expType;
        },
        async changeDate(type: string) {
            if (this.computedStartDate != null && this.computedExpirationDate != null) {
                if (type === 'startdate') {
                    this.checkExpirationDate();
                }
                if (type === 'expirationdate') {
                    this.checkDuration();
                }
                this.$emit('changeDate', { startDate: this.computedStartDate,
                                            endDate: this.computedExpirationDate });
            }
        },
        checkDuration() {
            if (this.selected != null && this.selected.expirationType != null) {
                const type = this.getExpirationPeriodType(this.selected.expirationType);
                if (this.selected.hasOwnProperty('duration')
                && this.selected.requiresDuration
                && this.selected.duration != null
                && this.selected.duration > 0) {
                    const startDate = this.computedStartDate!;
                    const endDate = this.computedExpirationDate!;
                    if (type === ExpirationPeriodType.Monthly) {
                        if (countDays(endDate, startDate) > getDaysMonth(startDate, this.selected.duration)) {
                            let diff = Math.floor((12 * (endDate.getFullYear() - startDate.getFullYear()))
                                + (endDate.getMonth() - startDate.getMonth()));
                            if (countDays(endDate, startDate) > getDaysMonth(startDate, diff)) {
                                diff = diff + 1;
                            }
                            this.duration = diff;
                        }
                    }
                    if (type === ExpirationPeriodType.Daily) {
                        const diff = Math.floor(
                            (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 3600 * 24));
                        if (diff !== this.selected.duration) {
                            this.duration = diff;
                        }
                    }
                }
            }
        },
        checkExpirationDate() {
            if (this.selected != null) {
                const type = this.selected?.expirationType
                ? this.getExpirationPeriodType(this.selected.expirationType)
                : null;

                const startDate: Date = this.computedStartDate || new Date();

                const period = this.selected.hasOwnProperty('expirationPeriodCount')
                    && this.selected.expirationPeriodCount != null
                    ? Number(this.selected.expirationPeriodCount)
                    : this.selected.hasOwnProperty('duration') && this.selected.duration != null
                        ? Number(this.selected.duration) > 0 ? Number(this.selected.duration) : 1
                        : 1 ;

                const firstDay = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                const isFirstDay = startDate.getTime() === firstDay.getTime() ? true : false;

                let permitTypeExpiration: Date | null = null;

                if (this.selected.hasOwnProperty('expirationDate')
                && this.selected.expirationDate != null) {
                    permitTypeExpiration = new Date(this.selected.expirationDate);
                    permitTypeExpiration = new Date(permitTypeExpiration.getFullYear(),
                        permitTypeExpiration.getMonth(),
                        permitTypeExpiration.getDate());
                }

                if (type === null) {
                    this.maxEndDate = permitTypeExpiration ? permitTypeExpiration : this.computedExpirationDate;
                }

                if (type === ExpirationPeriodType.SpecificDate) {
                    this.maxEndDate = this.computedExpirationDate;
                }

                if (type === ExpirationPeriodType.NonExpiring) {
                    this.maxEndDate = permitTypeExpiration ? permitTypeExpiration : this.computedExpirationDate;
                }

                if (type === ExpirationPeriodType.Annual) {
                    const endDate = new Date(startDate.getFullYear() + 1, startDate.getMonth(), startDate.getDate());
                    this.computedExpirationDate = endDate;
                    this.maxEndDate = endDate;
                }

                if (type === ExpirationPeriodType.Monthly) {
                    const endDate = isFirstDay
                    ? new Date(startDate.getFullYear(), startDate.getMonth() + period, 0)
                    : new Date(startDate.getFullYear(), startDate.getMonth() + period, startDate.getDate());

                    this.computedExpirationDate = endDate;
                    this.maxEndDate = this.selected.requiresDuration ? permitTypeExpiration : endDate;
                }

                if (type === ExpirationPeriodType.Quarterly) {
                    const q = Math.floor(startDate.getMonth() / 3);
                    const endDate = new Date(startDate.getFullYear(), (q * 3) + (period * 3), 0);

                    this.computedExpirationDate = endDate;
                    this.maxEndDate = this.selected.requiresDuration ? permitTypeExpiration : endDate;
                }

                if (type === ExpirationPeriodType.Daily) {
                    const endDate =  new Date(startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate() + period);

                    this.computedExpirationDate = endDate;
                    this.maxEndDate = this.selected.requiresDuration ? permitTypeExpiration : endDate;
                }

                if (permitTypeExpiration != null
                && this.computedExpirationDate != null
                && this.computedExpirationDate > permitTypeExpiration) {
                    this.computedExpirationDate = permitTypeExpiration;
                    this.maxEndDate = permitTypeExpiration;
                }
            }
        },
        async getExpirationDate(): Promise<Date | null> {
            const token = this.createCancelToken('getExpirationDate');
            try {
                return (await VatsAPI.get<Date>(
                    `/${this.location}/Permits/expirationDateForPermitType`,
                    {
                        params: {
                            description: this.selectedPermitTypeID,
                            issueDate: (new Date()).toISOString(),
                            duration: this.selected!.requiresDuration ? this.duration : 0
                        },
                        cancelToken: token
                    }
                )
                ).data;
            }
            catch (e) {
                this.throwIfNotCancel(e);

                return null;
            }
        },
        async getStartDate(): Promise<Date | null> {
            const token = this.createCancelToken('getStartDate');

            try {
                return (await VatsAPI.get<Date>(
                    `/${this.location}/Permits/startDateForPermitType`,
                    {
                        params: {
                            description: this.selectedPermitTypeID,
                            issueDate: (new Date()).toISOString(),
                        },
                        cancelToken: token
                    }
                )
                ).data;
            }
            catch (e) {
                this.throwIfNotCancel(e);

                return null;
            }
        },
        getExpirationTypeLabelFromPermitExpirationType(expirationTypeString: string): string {
            const expirationType = this.getExpirationPeriodType(expirationTypeString);
            return expirationType.toString();
        },
        enableOverrides() {
            this.overridesEnabled = true;
        },
        async cancelOverrides() {
            this.overridesEnabled = false;

            const permitType = (
                await VatsAPI.get<PermitType>(
                    `/${this.location}/Permits/type`, { params: { description: this.selectedPermitTypeID } }
                )
            ).data;

            this.$emit('change', permitType);
        },
        confirmOverrides() {
            this.overridesEnabled = false;

            this.$emit('change', this.selected);
        }
    }
});

export default PermitTypeSelect;

